import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Layers, LogOut, Users } from 'lucide-react'
import type { TerminalTheme } from '@apna/shared'
import { useAuth } from '@/store/auth'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { useSidebarState } from '@/components/ui/sidebar'

/**
 * Who is signed in, and everything that is configured once per session.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SETTINGS BELONG AT THE BOTTOM, NOT IN THE TRADING CHROME.
 *
 * Broker setup, the strategy library, the theme and signing out are all
 * things a person does between sessions, not during one. Spread across the
 * rail's nav and the top bar they competed for attention with the market
 * pill, the feed age and the account selector — the four things that must be
 * readable at a glance while an order is live. Collapsing them into one menu
 * under the avatar gives the rail back two slots and the top bar two.
 *
 * WHAT DID NOT MOVE: the account selector. Which broker account an order goes
 * to is a live trading control that changes within a session and must be
 * visible without a click, so it stays in the top bar beside the mode badge.
 *
 * THE TRIGGER FOLLOWS THE SIDEBAR. Collapsed it is the avatar alone, as it
 * always was; expanded it carries the name and the address beside it. The
 * menu opens to the `right` either way, because there is nothing to its left.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** "Ahmed Solanki" → "AS". One letter when there is only one word. */
export function initialsOf(name: string | null, email: string): string {
  const source = (name ?? '').trim() || email.split('@')[0]!.replace(/[._-]+/g, ' ')
  const parts = source.split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return ((first + last).toUpperCase() || 'U').slice(0, 2)
}

const NAV = [
  { to: '/accounts', icon: Users, label: 'Broker Accounts', testid: 'menu-accounts' },
  { to: '/strategies', icon: Layers, label: 'Strategies', testid: 'menu-strategies' },
] as const

/**
 * Terminal theme — THREE, not a toggle.
 *
 * A two-state switch cannot express three options, and Bloomberg Pro is not a
 * shade of dark: it changes the type family and the corner radius too. So this
 * row is three swatches rather than a sun/moon switch — the same place in the
 * menu, but a control that can say what it means.
 */
const THEMES: { key: TerminalTheme; name: string; swatch: string }[] = [
  { key: 'light', name: 'Calm White', swatch: '#F4F6F9' },
  { key: 'dark', name: 'Midnight', swatch: '#0A0D14' },
  { key: 'bloomberg', name: 'Bloomberg Pro', swatch: '#FFB000' },
]

/**
 * `expanded` is read from the sidebar when there is one.
 *
 * `useSidebarState` rather than `useSidebar` directly, because this is also
 * rendered in places with no sidebar around it — the hook must answer
 * "collapsed" there instead of throwing, which `useSidebar` does by design.
 */
export function NavUser({ expanded: forced }: { expanded?: boolean } = {}) {
  const navigate = useNavigate()
  const fromSidebar = useSidebarState()
  const expanded = forced ?? fromSidebar
  const user = useAuth((s) => s.user)
  const logout = useAuth((s) => s.logout)
  const setTheme = useAuth((s) => s.setTheme)
  const theme = user?.theme ?? 'dark'
  const [confirmSignOut, setConfirmSignOut] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  if (!user) return null
  const initials = initialsOf(user.name, user.email)

  /*
   * The store clears local state in its own `finally`, so by the time this
   * resolves OR rejects the session is over in this tab. Leaving for /login
   * either way is the honest outcome — the alternative, which is what the
   * rail's old button did, drops the rejection on the floor and leaves a
   * signed-out user looking at a live terminal.
   */
  const signOut = async (): Promise<void> => {
    setSigningOut(true)
    try {
      await logout()
    } catch {
      // Deliberately swallowed; see above.
    } finally {
      navigate('/login')
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Account and settings"
            data-testid="nav-user"
            className={cn(
              'flex items-center gap-2.5 rounded-lg border border-border bg-card text-[12px] font-semibold text-foreground transition-colors hover:bg-secondary data-[state=open]:bg-secondary',
              // Collapsed it is the avatar alone, as it always was; expanded
              // it grows to carry the name and the address, because at 16rem
              // a lone square of initials is a riddle.
              expanded ? 'h-11 w-full px-2' : 'h-10 w-10 justify-center',
            )}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary">
              {initials}
            </span>
            {expanded && (
              <span className="min-w-0 flex-1 text-left">
                <span className="block truncate text-[12.5px] font-semibold leading-tight">
                  {user.name ?? user.email.split('@')[0]}
                </span>
                <span className="block truncate text-[10.5px] font-normal leading-tight text-muted-foreground">
                  {user.email}
                </span>
              </span>
            )}
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side="right"
          align="end"
          sideOffset={8}
          className="w-64 p-0"
          data-testid="nav-user-menu"
        >
          <div className="flex items-center gap-2.5 px-3 py-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-secondary text-[12px] font-semibold">
              {initials}
            </span>
            <span className="grid min-w-0 flex-1 leading-tight">
              <span className="truncate text-[13px] font-medium">
                {user.name ?? user.email.split('@')[0]}
              </span>
              <span className="truncate text-[11px] text-muted-foreground">{user.email}</span>
            </span>
            <span className="qbar-badge shrink-0 border-border/60 text-muted-foreground">
              {user.role}
            </span>
          </div>

          <DropdownMenuSeparator className="my-0" />

          {NAV.map(({ to, icon: Icon, label, testid }) => (
            <DropdownMenuItem
              key={to}
              onSelect={() => navigate(to)}
              data-testid={testid}
              className="cursor-pointer gap-2.5 rounded-none px-3 py-2 text-[13px]"
            >
              <Icon size={15} className="text-muted-foreground" />
              {label}
            </DropdownMenuItem>
          ))}
          {user.role === 'OWNER' ? <DropdownMenuItem onSelect={() => navigate('/simulation')} data-testid="menu-simulation" className="cursor-pointer gap-2.5 rounded-none px-3 py-2 text-[13px]">NIFTY simulation</DropdownMenuItem> : null}
          {user.role === 'OWNER' ? <DropdownMenuItem onSelect={() => navigate('/traders')} data-testid="menu-traders" className="cursor-pointer gap-2.5 rounded-none px-3 py-2 text-[13px]">All traders</DropdownMenuItem> : null}

          <DropdownMenuSeparator className="my-0" />

          {/*
            A plain row, not `DropdownMenuItem`s: Radix closes the menu on
            select, and picking a theme is the one thing here you want to see
            applied before deciding whether to keep it.
          */}
          <div className="flex items-center justify-between gap-3 px-3 py-2.5">
            <span className="text-[13px]">Theme</span>
            <span className="flex items-center gap-1.5" data-testid="theme-swatches">
              {THEMES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => void setTheme(t.key)}
                  title={t.name}
                  aria-label={t.name}
                  aria-pressed={theme === t.key}
                  data-testid={`theme-option-${t.key}`}
                  className={cn(
                    'h-5 w-5 rounded-md border transition-all',
                    theme === t.key
                      ? 'border-primary ring-2 ring-primary/30'
                      : 'border-border hover:border-[var(--tr-line3)]',
                  )}
                  style={{ background: t.swatch }}
                />
              ))}
            </span>
          </div>

          <DropdownMenuSeparator className="my-0" />

          <DropdownMenuItem
            onSelect={() => setConfirmSignOut(true)}
            data-testid="nav-logout"
            className="cursor-pointer gap-2.5 rounded-none px-3 py-2 text-[13px] font-semibold text-destructive focus:text-destructive"
          >
            <LogOut size={15} className="text-destructive" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/*
        OUTSIDE the menu, not inside it. Radix returns focus to the trigger as
        the menu closes, and a dialog mounted within that subtree fights it for
        focus — the confirm button ends up unreachable by keyboard.

        Signing out is confirmed because of what it costs on THIS screen rather
        than because it is hard to undo: the socket closes, every subscription
        is dropped and the chain goes blank, so a misclick during market hours
        is half a minute of reconnecting with positions open.
      */}
      <ConfirmDialog
        open={confirmSignOut}
        /* Not dismissable mid-flight: the request is already in the air. */
        onOpenChange={(o) => !signingOut && setConfirmSignOut(o)}
        title="Sign out?"
        description="The market feed will disconnect and this tab will stop tracking your positions. Anything already working at the broker stays working — signing out does not cancel orders or close positions."
        confirmLabel="Sign out"
        pendingLabel="Signing out…"
        isPending={signingOut}
        onConfirm={() => void signOut()}
      />
    </>
  )
}
